import React, {useEffect, useMemo, useRef, useState} from 'react'
import TitleDescriptionInput from '../../components/customInput/TitleDescriptionInput'
import {useAppDispatch, useAppSelector} from '../../hooks/redux'

import {getSubjectId} from '../biomarkers/[id]/IndicatorsAndSignals'
import {initCurrent, updateCurrent} from '../../redux/slice/currentItem'
import {useLocation} from 'react-router-dom'
import Editor from "../../components/CKEditor/editor";

export const MAX_URL_LENGTH = 22

const SubjectEdit = ({ language = 'en' }: { language?: string }) => {
    const dispatch = useAppDispatch()
    const {subjects} = useAppSelector((state: any) => state.data)

    const {currentItem} = useAppSelector((state: any) => state)

    const subjectId = getSubjectId();
    const [isInitialized, setIsInitialized] = useState(false);
    const [isUserEditing, setIsUserEditing] = useState(false);
    const [localDescription, setLocalDescription] = useState<string>('');
    const [localBibliography, setLocalBibliography] = useState<string>('');
    
    useEffect(() => {
        dispatch(initCurrent({data: subjects[subjectId], type: 'subject'}))
        // Устанавливаем флаг инициализации после небольшой задержки
        setTimeout(() => {
            setIsInitialized(true);
        }, 100);
    }, [subjectId, subjects, dispatch])
    
    // Сбрасываем флаг редактирования при смене языка
    useEffect(() => {
        setIsUserEditing(false);
        
        // Дополнительная задержка для полного сброса состояния
        const timeoutId = setTimeout(() => {
            setIsUserEditing(false);
        }, 50);
        
        return () => clearTimeout(timeoutId);
    }, [language])

    // Очистка при размонтировании
    useEffect(() => {
        return () => {
            // Cleanup if needed
        };
    }, [])


    const handleUpdateSubjects = (item: any) => {
        dispatch(updateCurrent(item));
    }

    const handleLanguageChange = (newLanguage: string) => {
        if (newLanguage === language) return
        
        // Сохраняем текущее значение description в текущем языке
        if (localDescription !== undefined && localDescription !== null) {
            const currentDescriptionObj = currentItem?.data?.description && typeof currentItem.data.description === 'object' 
                ? { ...currentItem.data.description, [language]: localDescription }
                : { [language]: localDescription }
            
            dispatch(updateCurrent({
                description: currentDescriptionObj
            }))
        }
    }

    let isEditor = (global as any)?.isEditor;

    // Мультиязычные значения
    const name = (() => {
        if (currentItem?.data?.name && typeof currentItem.data.name === 'object') {
            return currentItem.data.name[language] || ''
        }
        
        const fallbackValue = language === 'en'
        ? currentItem?.data?.name || ''
            : (currentItem?.data?.[`name_${language}`] || '')
        
        return fallbackValue
    })()
    
    // Простое вычисление без useMemo
    const description = (() => {
        if (currentItem?.data?.description && typeof currentItem.data.description === 'object') {
            return currentItem.data.description[language] || ''
        }
        
        const fallbackValue = language === 'en'
        ? currentItem?.data?.description || ''
            : (currentItem?.data?.[`description_${language}`] || '')

        return fallbackValue
    })()
    
    const bibliography = (() => {
        if (typeof currentItem?.data?.bibliography === 'object' && currentItem?.data?.bibliography !== null) {
            return currentItem.data.bibliography['en'] || currentItem.data.bibliography[Object.keys(currentItem.data.bibliography)[0]] || ''
        }
        return currentItem?.data?.bibliography || ''
    })()

    // Синхронизируем локальное состояние с данными из Redux при смене языка
    useEffect(() => {
        setLocalDescription(description);
    }, [description, language])

    // Синхронизируем bibliography только при изменении данных, но не при смене языка
    useEffect(() => {
        setLocalBibliography(bibliography);
    }, [bibliography])

    return (<>

        <TitleDescriptionInput
            name={name}
            description={localDescription}
            files={currentItem?.data?.files || []}
            onStartEditing={() => setIsUserEditing(true)}
            callback={(data) => {
                if (!isInitialized || !isUserEditing) {
                    return;
                }
                
                const update: any = {};
                
                if (data.name !== undefined) {
                    if (currentItem?.data?.name && typeof currentItem.data.name === 'object') {
                        const updatedName = { ...currentItem.data.name, [language]: data.name };
                        update.name = updatedName;
                    } else {
                        // Если name - строка, сохраняем как есть
                        update.name = data.name;
                    }
                }       

                
                if (data.description !== undefined) {
                    setLocalDescription(data.description);
                    if (currentItem?.data?.description && typeof currentItem.data.description === 'object') {
                        const updatedDescription = { ...currentItem.data.description, [language]: data.description };
                        update.description = updatedDescription;
                    } else {
                        // Если description - строка, сохраняем как есть
                        update.description = data.description;
                    }
                }
                
                if (data.files !== undefined) update.files = data.files;
                
                if (Object.keys(update).length > 0) {
                    handleUpdateSubjects(update);
                }
            }}
        />
        <h3 style={{marginTop: '40px'}}>Bibliography:</h3>
        <div className={'bibliography'}>
            <Editor
                value={localBibliography}
                onChange={(value: any) => {
                    setLocalBibliography(value);
                    
                    if (!isInitialized) {
                        return;
                    }
                    
                    if (typeof currentItem?.data?.bibliography === 'object' && currentItem?.data?.bibliography !== null) {
                        const updatedBibliography = { ...currentItem.data.bibliography, ['en']: value };
                        handleUpdateSubjects({ bibliography: updatedBibliography });
                    } else {
                        handleUpdateSubjects({ bibliography: value });
                    }
                }}
                placeholder={'Bibliography list ...'}
            />
        </div>
    </>

    )
}
export default SubjectEdit
